// TD: cart cross-sell slider + shipping protection
import { Cart } from "@troop-themes/main";

(() => {
  function themeOn(eventName, handler, namespace) {
    const key = `${eventName}.${namespace}`;
    window.on(key, handler);
    return () => window.off(key, handler);
  }

  function themeOnCartUpdated(cartRoot, handler, namespace) {
    return themeOn(
      "theme:cart:updated",
      (event) => {
        if (event.detail != null && event.detail !== cartRoot) return;
        handler(event);
      },
      namespace,
    );
  }

  function cartPostItems(items, openDrawer = null) {
    return Cart.addItems(
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items, sections: Cart.sections }),
      },
      openDrawer,
    );
  }

  if (!customElements.get("td-cart-cross-sell")) {
    const crossSellSelectors = {
      addButton: "[data-td-add-button]",
      addButtonText: "[data-td-add-button-text]",
      apiRoot: "[data-td-cross-sell-api-root]",
      cartLineItem: "[data-td-cart-item][data-product-id]",
      productCard: "[data-td-cross-sell-card]",
      productIdsMarker: "template[data-td-product-ids]",
      navWrapper: "[data-td-swiper-nav]",
      comparePrice: "[data-td-price-compare]",
      currentPrice: "[data-td-price-current]",
      swiperViewport: "[data-swiper]",
      slidesTrack: "[data-td-cross-sell-track]",
      cartBody: ".cart--body",
      slide: "[data-swiper-slide]",
      swiperComponent: "swiper-slider",
      titleDefault: "[data-td-title-default]",
      titleEmptyCart: "[data-td-title-empty]",
      variantSelect: "[data-td-variant-select]",
    };

    const crossSellRecommendationsSectionId = "td-cart-cross-sell-recommendations";

    function productIdsKeyFromApiRoot(root) {
      if (!root) return "";
      return (
        root.getAttribute("data-td-product-ids") ||
        root.querySelector(crossSellSelectors.productIdsMarker)?.getAttribute("data-td-product-ids") ||
        ""
      );
    }

    /** Shared parser for `td-cart-cross-sell-recommendations` HTML (both storefront `?section_id=` and `product_recommendations` responses). */
    function parseCrossSellSectionDocument(htmlText) {
      const doc = new DOMParser().parseFromString(htmlText, "text/html");
      const root = doc.querySelector(crossSellSelectors.apiRoot);
      if (!root) return { slides: [], productIdsKey: "" };
      return {
        slides: [...root.querySelectorAll(crossSellSelectors.slide)].map((node) => node.cloneNode(true)),
        productIdsKey: productIdsKeyFromApiRoot(root),
      };
    }

    class TDCartCrossSell extends HTMLElement {
      static index = 0;

      constructor() {
        super();
        this.instanceId = TDCartCrossSell.index += 1;
        this._onProductAdded = (event) => {
          if (event.detail != null) this.lastAddedVariantId = String(event.detail);
        };
        this._onClick = (event) => this.onAddClick(event);
        this._onVariantChange = (event) => this.onVariantChange(event);
      }

      connectedCallback() {
        this.cartRoot = this.closest("cart-element");
        this.slider = this.querySelector(crossSellSelectors.swiperViewport);
        this.sliderTrack = this.querySelector(crossSellSelectors.slidesTrack);
        this.swiperSlider = this.querySelector(crossSellSelectors.swiperComponent);
        this.nav = this.querySelector(crossSellSelectors.navWrapper);
        this.titleDefaultNode = this.querySelector(crossSellSelectors.titleDefault);
        this.titleEmptyNode = this.querySelector(crossSellSelectors.titleEmptyCart);
        if (!this.cartRoot || !this.slider || !this.sliderTrack || !this.swiperSlider) return;

        this.source = this.dataset.tdSource || "manual";
        this.maxProducts = Number.parseInt(this.dataset.tdMaxProducts, 10) || 6;
        this.showOnEmptyCart = this.dataset.tdShowOnEmpty === "true";
        this.lastAddedVariantId = "";
        this.lastRenderedKey = "";
        this.lastCartCrossSellFingerprint = "";
        this.crossSellSectionId =
          this.dataset.tdCrossSellSectionId || crossSellRecommendationsSectionId;
        this._crossSellUpdateRunning = false;
        this._crossSellUpdateQueued = false;
        this.stampedRefreshTimer = null;

        this.sliderTrack.addEventListener("click", this._onClick);
        this.sliderTrack.addEventListener("change", this._onVariantChange);

        const namespace = `TDCartCrossSell${this.instanceId}`;
        this._unsubs = [
          themeOn("theme:cart:productAdded", this._onProductAdded, namespace),
          themeOnCartUpdated(this.cartRoot, () => this.onCartSectionUpdated(), namespace),
        ];

        this.lastCartCrossSellFingerprint = this.cartCrossSellFingerprint();
        this.hydrateFromSSR();
        if (
          this.source === "search_discovery" &&
          !this.sliderTrack.querySelector(crossSellSelectors.slide)
        ) {
          this.queueCrossSellUpdate();
        }
      }

      disconnectedCallback() {
        this.sliderTrack?.removeEventListener("click", this._onClick);
        this.sliderTrack?.removeEventListener("change", this._onVariantChange);
        this._unsubs?.forEach((unsubscribe) => unsubscribe());
        if (this.stampedRefreshTimer) window.clearTimeout(this.stampedRefreshTimer);
      }

      queueCrossSellUpdate() {
        if (this._crossSellUpdateRunning) {
          this._crossSellUpdateQueued = true;
          return;
        }
        let snapshot = null;
        if (this.source !== "search_discovery") {
          snapshot = this.cartCrossSellFingerprint();
          if (snapshot === this.lastCartCrossSellFingerprint) return;
        }
        this._crossSellUpdateRunning = true;
        const work =
          this.source === "search_discovery"
            ? this.refresh()
            : this.applyCrossSellFragment(snapshot);
        work
          .catch((err) =>
            console.error(
              this.source === "search_discovery"
                ? "Cart cross-sell refresh failed"
                : "Cart cross-sell fragment refresh failed",
              err,
            ),
          )
          .finally(() => {
            this._crossSellUpdateRunning = false;
            if (this._crossSellUpdateQueued) {
              this._crossSellUpdateQueued = false;
              this.queueCrossSellUpdate();
            }
          });
      }

      onCartSectionUpdated() {
        this.queueCrossSellUpdate();
      }

      cartCrossSellFingerprint() {
        const body = this.cartRoot.querySelector(crossSellSelectors.cartBody);
        if (body?.hasAttribute("data-td-cart-product-ids")) {
          const empty = body.getAttribute("data-td-cart-empty") === "true";
          const ids = body.getAttribute("data-td-cart-product-ids") ?? "";
          return `${empty ? "e" : "n"}:${ids}`;
        }
        return '';
      }

      async applyCrossSellFragment(snapshot) {
        const empty = this.isCartEmptyForCrossSell();
        this.swapTitles(empty);

        if (empty && !this.showOnEmptyCart) {
          this.hide();
          this.lastCartCrossSellFingerprint = snapshot;
          return;
        }

        const res = await this.fetchStorefrontCrossSellSectionResponse();
        if (!res.ok) throw new Error(res.statusText);
        if (this.cartCrossSellFingerprint() !== snapshot) return;

        const { slides, productIdsKey } = parseCrossSellSectionDocument(await res.text());
        this.renderSlideElements(slides, productIdsKey);
        this.lastCartCrossSellFingerprint = snapshot;
      }

      fetchStorefrontCrossSellSectionResponse() {
        const rootPath = window.Shopify?.routes?.root ?? "/";
        const url = new URL(rootPath, window.location.href);
        url.searchParams.set("section_id", this.crossSellSectionId);
        return fetch(url.toString());
      }

      hydrateFromSSR() {
        const empty = this.isCartEmptyForCrossSell();
        if (empty && !this.showOnEmptyCart) {
          this.hide();
          return;
        }
        const slides = [...this.sliderTrack.querySelectorAll(crossSellSelectors.slide)];
        this.renderSlideElements(slides, this.readProductIdsKeyFromTrack());
      }

      async refresh() {
        const empty = this.isCartEmptyForCrossSell();
        this.swapTitles(empty);
        if (empty && !this.showOnEmptyCart) {
          this.hide();
          return;
        }
        if (empty && this.showOnEmptyCart) {
          try {
            const res = await this.fetchStorefrontCrossSellSectionResponse();
            if (!res.ok) throw new Error(res.statusText);
            const { slides, productIdsKey } = parseCrossSellSectionDocument(await res.text());
            this.renderSlideElements(slides, productIdsKey);
          } catch (err) {
            console.error("Cart cross-sell empty-cart section fetch failed", err);
            this.hydrateFromSSR();
          }
          return;
        }
        const cartItems = this.getCartItems();
        const { slides, productIdsKey } = await this.fetchRecommendationSlides(cartItems);
        this.renderSlideElements(slides, productIdsKey);
      }

      isCartEmptyForCrossSell() {
        const empty_attr = document.querySelector("[data-cart-empty]")?.getAttribute("data-cart-empty");
        if (empty_attr) return empty_attr === "true";
        return false;
      }

      hide() {
        this.setAttribute("aria-hidden", "true");
        this.slider?.setAttribute("aria-hidden", "true");
        this.nav?.setAttribute("aria-hidden", "true");
        this.lastRenderedKey = "";
      }

      show() {
        this.setAttribute("aria-hidden", "false");
        this.slider?.setAttribute("aria-hidden", "false");
        this.nav?.removeAttribute("aria-hidden");
      }

      swapTitles(isEmpty) {
        const defaultTitle = this.titleDefaultNode;
        const emptyTitle = this.titleEmptyNode;
        if (!defaultTitle || !emptyTitle) return;
        if (isEmpty) {
          defaultTitle.setAttribute("hidden", "hidden");
          emptyTitle.removeAttribute("hidden");
        } else {
          emptyTitle.setAttribute("hidden", "hidden");
          defaultTitle.removeAttribute("hidden");
        }
      }

      getCartItems() {
        return [...this.cartRoot.querySelectorAll(crossSellSelectors.cartLineItem)]
          .map((line) => ({
            productId: Number.parseInt(line.getAttribute("data-product-id"), 10),
            variantId: String(line.getAttribute("data-variant-id") || ""),
          }))
          .filter((line) => !Number.isNaN(line.productId));
      }

      readProductIdsKeyFromTrack() {
        return this.sliderTrack?.querySelector(crossSellSelectors.productIdsMarker)?.getAttribute("data-td-product-ids") ?? "";
      }

      async fetchRecommendationSlides(cartItems) {
        const empty = { slides: [], productIdsKey: "" };
        const seed = this.seedId(cartItems);
        if (!seed) return empty;
        const res = await fetch(
          `${Shopify.routes.product_recommendations}?section_id=${crossSellRecommendationsSectionId}&product_id=${seed}&limit=${this.maxProducts}`,
        );
        if (!res.ok) return empty;
        return parseCrossSellSectionDocument(await res.text());
      }

      seedId(items) {
        if (this.lastAddedVariantId) {
          const match = items.find((line) => line.variantId === this.lastAddedVariantId);
          if (match) return match.productId;
        }
        return items.length ? items[items.length - 1].productId : null;
      }

      renderSlideElements(slides, productIdsKey = "") {
        if (!slides.length) {
          this.hide();
          this.lastRenderedKey = "";
          return;
        }
        this.show();
        if (productIdsKey && productIdsKey === this.lastRenderedKey) {
          this.syncSwiper();
          return;
        }
        this.sliderTrack.replaceChildren(...slides);
        this.lastRenderedKey = productIdsKey;
        this.stampedSoon();
        this.syncSwiper();
      }

      syncSwiper() {
        const swiperInstance = this.swiperSlider.slider;
        if (!swiperInstance || !this.sliderTrack.querySelector(crossSellSelectors.slide)) return;
        swiperInstance.update();
        swiperInstance.slideTo(0, 0, false);
      }

      stampedSoon() {
        if (!this.querySelector(".stamped-product-reviews-badge")) return;
        if (this.stampedRefreshTimer) window.clearTimeout(this.stampedRefreshTimer);
        this.stampedRefreshTimer = window.setTimeout(() => {
          if (!this.isConnected || typeof window.StampedFn?.loadBadges !== "function") return;
          window.StampedFn.loadBadges();
        }, 0);
      }

      onVariantChange(event) {
        const target = event.target;
        if (!(target instanceof Element)) return;
        const select = target.closest(crossSellSelectors.variantSelect);
        if (!select || !(select instanceof HTMLSelectElement)) return;
        const card = select.closest(crossSellSelectors.productCard);
        if (!card) return;
        const addButton = card.querySelector(crossSellSelectors.addButton);
        const addLabel = card.querySelector(crossSellSelectors.addButtonText);
        const priceEl = card.querySelector(crossSellSelectors.currentPrice);
        const compareEl = card.querySelector(crossSellSelectors.comparePrice);
        const selectedOption = select.options[select.selectedIndex];
        if (!addButton || !addLabel || !priceEl || !compareEl) return;
        if (!select.value) {
          addButton.setAttribute("disabled", "disabled");
          addButton.dataset.tdSelectedVariantId = "";
          addLabel.textContent = addButton.dataset.tdChooseSizeText || "Choose size";
          return;
        }
        addButton.dataset.tdSelectedVariantId = select.value;
        addButton.removeAttribute("disabled");
        addLabel.textContent = addButton.dataset.tdAddText || "Add to cart";
        priceEl.innerHTML = selectedOption.dataset.tdPrice || "";
        if (selectedOption.dataset.tdComparePrice) {
          compareEl.hidden = false;
          compareEl.innerHTML = selectedOption.dataset.tdComparePrice;
        } else {
          compareEl.hidden = true;
          compareEl.innerHTML = "";
        }
      }

      async onAddClick(event) {
        const target = event.target;
        if (!(target instanceof Element)) return;
        const addButton = target.closest(crossSellSelectors.addButton);
        if (!addButton) return;
        event.preventDefault();
        if (addButton.hasAttribute("disabled") || addButton.getAttribute("aria-busy") === "true") return;
        const variantId = Number.parseInt(addButton.dataset.tdSelectedVariantId, 10);
        if (Number.isNaN(variantId)) return;
        addButton.setAttribute("aria-busy", "true");
        addButton.setAttribute("disabled", "disabled");
        try {
          await cartPostItems([{ id: variantId, quantity: 1 }], null);
        } catch (err) {
          console.error("Cart cross-sell add to cart failed", err);
        } finally {
          addButton.setAttribute("aria-busy", "false");
          if (addButton.dataset.tdSelectedVariantId) addButton.removeAttribute("disabled");
        }
      }
    }

    customElements.define("td-cart-cross-sell", TDCartCrossSell);
  }

  if (!customElements.get("td-cart-shipping-protection")) {
    const shippingSelectors = {
      cartLineItem: "[data-td-cart-item]",
      protectionToggle: "[data-td-shipping-protection-toggle]",
    };

    class TDCartShippingProtection extends HTMLElement {
      static index = 0;

      constructor() {
        super();
        this.instanceId = TDCartShippingProtection.index += 1;
        this.busy = false;
        this._onToggle = (event) => this.toggleLine(event);
      }

      connectedCallback() {
        this.cartRoot = this.closest("cart-element");
        this.toggle = this.querySelector(shippingSelectors.protectionToggle);
        this.variantId = Number.parseInt(this.dataset.tdVariantId, 10);
        if (!this.cartRoot || !this.toggle || Number.isNaN(this.variantId)) return;
        this.toggle.addEventListener("change", this._onToggle);
        this._unsub = themeOnCartUpdated(
          this.cartRoot,
          () => this.syncState(),
          `TDCartShippingProtection${this.instanceId}`,
        );
        this.syncState();
      }

      disconnectedCallback() {
        this.toggle?.removeEventListener("change", this._onToggle);
        this._unsub?.();
      }

      lineCount() {
        return this.cartRoot.querySelectorAll(shippingSelectors.cartLineItem).length;
      }

      syncDrawerSummarySpacer() {
        if (this.cartRoot?.dataset.view !== "drawer") return;
        if (typeof this.cartRoot.updateSummaryHeight !== "function") return;
        window.requestAnimationFrame(() => {
          if (!this.isConnected) return;
          this.cartRoot.updateSummaryHeight();
        });
      }

      inCart() {
        if (
          this.cartRoot.querySelector(
            `${shippingSelectors.cartLineItem}[data-variant-id="${this.variantId}"]`,
          )
        )
          return true;
        const qty = Cart.basket.get(this.variantId);
        return typeof qty === "number" && qty > 0;
      }

      setBusy(isBusy) {
        this.busy = isBusy;
        this.setAttribute("aria-busy", isBusy ? "true" : "false");
        this.toggle?.toggleAttribute("disabled", isBusy);
      }

      syncState() {
        const protectionInCart = this.inCart();
        this.dataset.tdInCart = protectionInCart ? "true" : "false";
        this.setAttribute("aria-hidden", this.lineCount() > 0 ? "false" : "true");
        this.toggle.checked = protectionInCart;
        this.syncDrawerSummarySpacer();
      }

      async toggleLine(event) {
        const input = event.target;
        if (!(input instanceof HTMLInputElement)) return;
        if (this.busy) {
          event.preventDefault();
          return;
        }
        this.setBusy(true);
        try {
          if (input.checked) {
            await cartPostItems(
              [{ id: this.variantId, quantity: 1, properties: { _td_shipping_protection: "true" } }],
              null,
            );
          } else {
            const response = await fetch(`${Shopify.routes.cart_update}.js`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ updates: { [this.variantId]: 0 }, sections: Cart.sections }),
            });
            if (!response.ok) throw new Error(response.statusText);
            const payload = await response.json();
            Cart.fillBasket(payload.items);
            Cart.updateTotals(payload.item_count);
            Cart.updateAllHtml(payload.sections, true);
          }
        } catch (err) {
          console.error("Shipping protection update failed", err);
        } finally {
          this.setBusy(false);
          this.syncState();
        }
      }
    }

    customElements.define("td-cart-shipping-protection", TDCartShippingProtection);
  }
})();
